import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import isNil from 'lodash/isNil';

import CampaignPopover from 'modules/freemium/components/campaign-popover/CampaignPopover';
import CollectionPopover from 'modules/freemium/components/collection-popover/CollectionPopover';
import InsightsPopover from 'modules/freemium/components/insights-popover/InsightsPopover';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import Tooltip from 'components/Tooltip';

import { addUserAction } from 'modules/tracking/providers/user-action-provider';

import { isPayoutRealm } from 'utils/account';
import { isActive } from 'utils/navigation';
import { __t, applyTranslations } from 'utils/language';

import './navigation.scss';

import type { NavigationNavItem, Organization, Role } from 'types';

const POPOVER_BY_FEATURE_NAME = {
  campaign: CampaignPopover,
  collection: CollectionPopover,
  insights: InsightsPopover
};

applyTranslations(require('components/navigation/navigation.i18n.json'));

type OwnProps = {
  items: Array<NavigationNavItem>,
  currentOrganization: Organization
};

type StateProps = {
  roles: Array<Role>,
  path: string
};

type Props = OwnProps & StateProps;

const MSG_KEY = 'navigation';

class Navigation extends React.Component<Props> {
  handleOnClick = (event: React.SyntheticEvent<HTMLLIElement>): void => {
    const target = event.currentTarget || {};
    const trackingKey = target.dataset.trackingKey || '';

    if (trackingKey) {
      addUserAction({
        eventCategory: 'navigation',
        eventAction: trackingKey
      });
    }
  };

  renderItem(item: NavigationNavItem, index: number, key?: string | null): React.ReactNode {
    const { path, currentOrganization, roles } = this.props;
    const qaKey = item.glossaryKey.toLowerCase().replace(/\s/, '-');
    const shouldLinkBeBlocked = currentOrganization.isFreemium && !item.isFreemiumAllowed;
    const isLinkActive = isActive(item, path);
    const linkProps: React.ComponentProps<'a'> = {};
    let shouldClickBeSuppressed = isLinkActive && currentOrganization.isFreemium;

    // COLLAB-16613: we invite payout_realm role users to a freemium brand to limit access
    // avoid suppressing link click in this case so user can navigate back to admin page
    if (item.trackingKey === 'admin' && isPayoutRealm(roles)) {
      shouldClickBeSuppressed = false;
    }

    if (item.external) {
      linkProps.target = '_blank';
      linkProps.rel = 'noopener noreferrer';
    }

    const itemClasses = classnames({
      'navigation__item': true,
      'navigation__item--external': item.external
    });

    const linkClasses = classnames({
      'navigation__link': true,
      'navigation__link--active': isLinkActive,
      'navigation__link--active-is-freemium': shouldClickBeSuppressed,
      'navigation__link--external': item.external,
      'navigation__link--is-freemium': shouldLinkBeBlocked
    });

    return (
      <li
        key={key}
        className={itemClasses}
        data-qa={`navigation-item-${qaKey}`}
        data-tracking-key={item.trackingKey}
        onClick={!shouldClickBeSuppressed && !shouldLinkBeBlocked ? this.handleOnClick : undefined}
      >
        <a
          href={!shouldLinkBeBlocked ? item.url : ''}
          className={linkClasses}
          {...linkProps}
        >
          {
            item.icon ?
              <SVGDynamicIcon
                customColor="white"
                name={item.icon.name}
                height={item.icon.width}
                width={item.icon.height}
              /> :
              item.glossaryKey
          }
        </a>
      </li>
    );
  }

  renderTooltip(item: NavigationNavItem, children: React.ReactNode): React.ReactNode {
    const Popover = item.key ? POPOVER_BY_FEATURE_NAME[item.key] : null;

    if (isNil(Popover)) {
      return null;
    }

    if (Popover) {
      return (
        <Popover
          key={`navigation-popover--${String(item.key)}`}
          triggerComponent={children}
        />
      );
    }

    return (
      <Tooltip
        key={`navigation-tooltip--${String(item.key)}`}
        message={__t(`${MSG_KEY}.tooltips.available_in_paid_version`)}
      >
        {children}
      </Tooltip>
    );
  }

  render(): React.ReactNode {
    const { items, currentOrganization } = this.props;

    return (
      <nav className="navigation">
        <ul className="navigation__items">
          {
            items.map((item, index) => {
              const key = `navigation__item-${item.trackingKey}`;

              return currentOrganization.isFreemium && !item.isFreemiumAllowed ?
                this.renderTooltip(item, this.renderItem(item, index)) :
                this.renderItem(item, index, key);
            })
          }
        </ul>
      </nav>
    );
  }
}

const mapStateToProps = ({ account, router }): StateProps => ({
  roles: account.roles,
  path: router.location.path
});

export default connect(mapStateToProps)(Navigation);
